import { CompanionAd } from '@quickplay/player-interface';
import { isHttpUrl } from './CompanionDecoder';
import { CompanionEvent, CompanionSize } from './CompanionTypes';
import { selectStaticCompanion, StaticCompanionView } from './StaticCompanionView';

interface StaticCompanionOptions {
  root: HTMLElement;
  sizes: CompanionSize[];
  timeoutMs: number;
  onChanged(): void;
  onAction(url: string, onAccepted: () => void): void;
  onEvent(event: CompanionEvent): void;
}

/** Owns one SSAI image and exposes companion trackers for the app to dispatch. */
export class StaticCompanionController {
  private candidates: CompanionAd[] = [];
  private current?: CompanionAd;
  private view?: StaticCompanionView;
  private timer?: ReturnType<typeof setTimeout>;
  private loaded = false;
  private readonly impressed = new Set<CompanionAd>();
  private ended = false;
  private retained = false;

  constructor(private readonly options: StaticCompanionOptions) {}

  get ready(): boolean {
    return this.loaded;
  }

  get isRetained(): boolean {
    return this.retained;
  }

  start(companions: CompanionAd[] = []): void {
    this.clear();
    this.candidates = companions.filter(c => isHttpUrl(c.staticResource));
    this.resize();
  }

  resize(): void {
    if (this.ended) return;
    const { root, sizes } = this.options;
    const selected = selectStaticCompanion(this.candidates, sizes, root.clientWidth);
    if (selected === this.current) return;
    this.removeView();
    this.current = selected;
    if (!selected) return;
    const fail = (): void => {
      clearTimeout(this.timer);
      this.removeView();
      this.options.onEvent({ type: 'AssetFailed', url: selected.staticResource });
      this.options.onChanged();
    };
    this.timer = setTimeout(fail, this.options.timeoutMs);
    this.view = new StaticCompanionView(selected, {
      onLoaded: () => {
        clearTimeout(this.timer);
        this.loaded = true;
        this.options.onChanged();
      },
      onFailed: fail,
      onClick: () => this.options.onAction(selected.clickThroughUrl!, () => this.track('click', selected)),
    });
    this.view.element.style.display = 'none';
    root.append(this.view.element);
  }

  show(visible: boolean): CompanionSize | undefined {
    const current = this.current;
    const fits = this.options.root.clientWidth > 0 && !!current && current.width <= this.options.root.clientWidth;
    const showing = visible && this.loaded && !!current && fits;
    if (this.view) this.view.element.style.display = showing ? 'block' : 'none';
    if (!showing || !current) return undefined;
    if (!this.impressed.has(current)) {
      this.impressed.add(current);
      this.track('impression', current);
    }
    if (current !== this.current) return undefined;
    return { width: current.width, height: current.height };
  }

  end(persist: boolean): void {
    this.ended = true;
    this.retained = persist && !!this.current && this.loaded && this.impressed.has(this.current);
    if (!this.retained) this.removeView();
  }

  clear(): void {
    this.removeView();
    this.candidates = [];
    this.current = undefined;
    this.impressed.clear();
    this.ended = false;
    this.retained = false;
  }

  private track(tracking: 'impression' | 'click', companion: CompanionAd): void {
    const urls = tracking === 'impression' ? companion.impressionTrackingUrls : companion.clickTrackingUrls;
    this.options.onEvent({
      type: 'TrackingRequested',
      tracking,
      companionId: companion.id,
      urls: [...new Set((urls ?? []).filter(isHttpUrl))],
    });
  }

  private removeView(): void {
    clearTimeout(this.timer);
    this.view?.dispose();
    this.view = undefined;
    this.loaded = false;
  }
}
